import type { SvgIconComponent } from '@mui/icons-material';
import { Button as MuiButton, type SxProps } from '@mui/material';
import { colors } from '@ovComponents/0-tokens/ref/palette/palette';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { type CSSProperties, useState } from 'react';
import { CircularProgress } from '../circularProgress/circularProgress';
import { useButtonTokens } from './button.tokens';

type ButtonProps = {
  color?: 'primary' | 'destructive';
  variant?: 'filled' | 'tonal' | 'outlined' | 'text';
  label: string;
  size?: 'xs' | 'sm' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  leadingIcon?: SvgIconComponent;
  trailingIcon?: SvgIconComponent;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: any) => void;
  children?: any;
  component?: any;
  href?: string;
  target?: string;
  to?: string;
  download?: boolean | string;
  loading?: boolean;
  dataTestId?: string;
};

export const Button = ({
  color = 'primary',
  variant = 'filled',
  label,
  size,
  fullWidth,
  onClick,
  disabled = false,
  sx,
  leadingIcon,
  trailingIcon,
  type = 'button',
  component,
  children,
  href,
  target,
  to,
  download,
  loading = false,
  dataTestId = 'button',
}: ButtonProps) => {
  const tokens = useThemeTokens(useButtonTokens());

  const [isHover, setIsHover] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleMouseDown = (e: any) => {
    e.preventDefault();
    setIsPressed(true);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  let variantStyle: CSSProperties = {};

  const backgroundColor = () => {
    if (isPressed) {
      return tokens.comp.button[variant][color].pressed.container.color;
    }
    if (isFocused) {
      return tokens.comp.button[variant][color].focused.container.color;
    }
    if (isHover) {
      return tokens.comp.button[variant][color].hovered.container.color;
    }
    if (disabled) {
      return tokens.comp.button[variant][color].disabled.container.color;
    }
    return tokens.comp.button[variant][color].enabled.container.color;
  };

  const textColor = () => {
    if (isPressed) {
      return tokens.comp.button[variant][color].pressed.text.color;
    }
    if (isHover) {
      return tokens.comp.button[variant][color].hovered.text.color;
    }
    if (isHover || isFocused) {
      return tokens.comp.button[variant][color].focused.text.color;
    }
    if (disabled) {
      return tokens.comp.button[variant][color].disabled.text.color;
    }
    return tokens.comp.button[variant][color].enabled.text.color;
  };

  const borderColor = () => {
    if (isPressed) {
      return tokens.comp.button[variant][color].pressed.container.borderColor;
    }
    if (disabled) {
      return tokens.comp.button[variant][color].disabled.container.borderColor;
    }
    if (isHover || isFocused) {
      return tokens.comp.button[variant][color].hovered.container.borderColor;
    }
    return tokens.comp.button[variant][color].enabled.container.borderColor;
  };

  switch (variant) {
    case 'filled':
    case 'tonal':
      variantStyle = {
        backgroundColor: backgroundColor(),
        color: textColor(),
        border: '1px solid transparent',
        borderColor: borderColor(),
      };
      break;
    case 'outlined':
      variantStyle = {
        backgroundColor: backgroundColor(),
        color: textColor(),
        border: '1px solid',
        borderColor: borderColor(),
      };
      break;
    case 'text':
      variantStyle = {
        backgroundColor: backgroundColor(),
        color: textColor(),
        border: '1px solid transparent',
      };
      break;
    default:
      variantStyle = {
        backgroundColor: !disabled
          ? isHover
            ? colors.primary600
            : colors.primary
          : colors.neutral400,
        color: !disabled ? colors.white : colors.neutral500,
        border: '1px solid transparent',
      };
  }

  const style: CSSProperties = {
    ...variantStyle,
    height:
      size === 'xs'
        ? tokens.comp.button.xs.height
        : size === 'sm'
          ? tokens.comp.button.sm.height
          : tokens.comp.button.lg.height,
    fontSize:
      size === 'xs'
        ? tokens.comp.button.xs.fontSize
        : size === 'sm'
          ? tokens.comp.button.sm.fontSize
          : tokens.comp.button.lg.fontSize,
    lineHeight: `${tokens.sys.spacing.xxl}px`,
    width: fullWidth ? '100%' : 'auto',
    padding:
      size === 'xs'
        ? tokens.comp.button.xs.padding
        : size === 'sm'
          ? tokens.comp.button.sm.padding
          : tokens.comp.button.lg.padding,
    borderRadius: tokens.comp.button.borderRadius,
    cursor: !disabled ? 'pointer' : 'auto',
    textTransform: 'none',
    outlineOffset: '2px',
    outline: isFocused ? `2px solid ${colors.focus}` : 'none',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  };

  return (
    <MuiButton
      href={href}
      download={download}
      data-testid={dataTestId}
      target={target}
      onClick={onClick}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled || loading}
      sx={sx}
      disableFocusRipple
      disableRipple
      startIcon={
        leadingIcon && !loading ? (
          <Icon icon={leadingIcon} size='small' />
        ) : null
      }
      endIcon={
        trailingIcon && !loading ? (
          <Icon icon={trailingIcon} size='small' />
        ) : null
      }
      type={type}
      component={component}
      to={to}
    >
      {!loading && label}
      {!loading && children}
      {loading && <CircularProgress size={20} sx={{ color: 'inherit' }} />}
    </MuiButton>
  );
};
